import React, { useState, useRef } from 'react';
import { ReactionType } from '~/src/types/posts';
import { UuidString, Uuid } from '~/src/utils/UUID';
import { Popover, ActionIcon, Text } from '@mantine/core';
import { SmilePlus } from 'lucide-react';
import { useGroupMembers } from '~/src/api_hooks/groups';

const REACTION_EMOJIS: Record<ReactionType, string> = {
    like: '👍',
    love: '❤️',
    laugh: '😂',
    wow: '😮',
    sad: '😢',
    angry: '😡',
};

interface ReactionIconProps {
    type: ReactionType;
}

export const ReactionIcon: React.FC<ReactionIconProps> = ({ type }) => {
    return <span>{REACTION_EMOJIS[type]}</span>;
};

interface ReactionPillProps {
    type: ReactionType;
    count: number;
    userReacted: boolean;
    onClick: () => void;
    users?: UuidString[]; // List of user IDs who reacted
    groupId?: UuidString; // Needed to resolve aliases
}

export const ReactionPill: React.FC<ReactionPillProps> = ({ type, count, userReacted, onClick, users = [], groupId }) => {
    // If groupId is not provided, the hook will skip fetching.
    const { data: groupMembers } = useGroupMembers(groupId);

    const [opened, setOpened] = useState(false);
    const longPressTimer = useRef<NodeJS.Timeout | null>(null);

    const handleTouchStart = () => {
        longPressTimer.current = setTimeout(() => {
            setOpened(true);
        }, 500); // 500ms for long press
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const resolveUserNames = () => {
        if (!groupMembers) return ['Loading...'];
        if (users.length === 0) return ['No users']; // Should not happen due to parent check
        return users.map(uid => {
            const member = groupMembers.find(gm => gm.user_id === uid);
            return member ? member.alias : `Unknown (${uid.toString().substring(0, 8)}...)`;
        });
    };

    const renderReactionList = () => {
        const names = resolveUserNames();
        const displayNames = names.slice(0, 5);
        const remaining = names.length - 5;

        // if (names.length === 0) return null; // Let's show empty state or whatever we have

        return (
            <div style={{ padding: '4px 8px' }}>
                {displayNames.map((n, i) => (
                    <Text key={i} size="xs" fw={500}>
                        {n}
                    </Text>
                ))}
                {remaining > 0 && (
                    <Text size="xs" fw={500} c="dimmed">
                        +{remaining} more
                    </Text>
                )}
            </div>
        );
    };

    return (
        <Popover
            width="auto"
            position="top"
            withArrow
            shadow="md"
            opened={opened}
            onChange={setOpened}
        >
            <Popover.Target>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        // If opened via long press (on mobile), clicking might toggle it closed or we might want to prevent click if it was a long press.
                        // Ideally, short tap = toggle reaction, long press = show details.
                        // On desktop, click = toggle reaction.
                        onClick();
                    }}
                    onMouseEnter={() => setOpened(true)}
                    onMouseLeave={() => setOpened(false)}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    // Prevent context menu on long press
                    onContextMenu={(e) => e.preventDefault()}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 6px',
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: userReacted ? '#4a90e2' : '#e0e0e0',
                        backgroundColor: userReacted ? '#f0f7ff' : 'transparent',
                        cursor: 'pointer',
                        fontSize: '12px',
                        lineHeight: '1',
                        transition: 'all 0.2s',
                        userSelect: 'none', // Important for long press
                        WebkitUserSelect: 'none',
                    }}
                >
                    <ReactionIcon type={type} />
                    <span style={{ color: userReacted ? '#4a90e2' : '#666', fontWeight: 500 }}>{count}</span>
                </button>
            </Popover.Target>
            <Popover.Dropdown style={{ pointerEvents: 'none' }}>
                {renderReactionList()}
            </Popover.Dropdown>
        </Popover>
    );
};

interface ReactionsDisplayProps {
    reactions: Map<ReactionType, Set<UuidString>>;
    currentUserId?: UuidString | null;
    onAddReaction: (type: ReactionType) => void;
    onRemoveReaction: (type: ReactionType) => void;
    groupId?: UuidString;
}

export const ReactionsDisplay: React.FC<ReactionsDisplayProps> = ({
    reactions,
    currentUserId,
    onAddReaction,
    onRemoveReaction,
    groupId
}) => {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', marginTop: '2px' }}>
            {Array.from(reactions.entries()).map(([type, usersSet]) => {
                if (usersSet.size === 0) return null;
                const userReacted = currentUserId ? usersSet.has(currentUserId) : false;
                const usersList = Array.from(usersSet);

                return (
                    <ReactionPill
                        key={type}
                        type={type}
                        count={usersSet.size}
                        userReacted={userReacted}
                        users={usersList}
                        groupId={groupId}
                        onClick={() => {
                            if (userReacted) {
                                onRemoveReaction(type);
                            } else {
                                onAddReaction(type);
                            }
                        }}
                    />
                );
            })}

            <ReactionPicker onSelect={onAddReaction} />
        </div>
    );
};

interface ReactionPickerProps {
    onSelect: (type: ReactionType) => void;
}

export const ReactionPicker: React.FC<ReactionPickerProps> = ({ onSelect }) => {
    return (
        <Popover width="auto" position="top" withArrow shadow="md">
            <Popover.Target>
                <ActionIcon variant="subtle" color="gray" size="sm" radius="xl" aria-label="Add reaction">
                    <SmilePlus size={16} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown style={{ padding: '8px', display: 'flex', gap: '8px' }}>
                {Object.entries(REACTION_EMOJIS).map(([type, emoji]) => (
                    <button
                        key={type}
                        onClick={() => onSelect(type as ReactionType)}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '20px',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            transition: 'transform 0.1s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                        {emoji}
                    </button>
                ))}
            </Popover.Dropdown>
        </Popover>
    );
};

